import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { getCollections } from '../lib/shopify';
import { ArrowRight } from 'lucide-react';

const CollectionsPage = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCollections = async () => {
      try {
        const data = await getCollections();
        setCollections(data || []);
      } catch (error) {
        console.error('Error fetching collections:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCollections();
  }, []);

  // Fallback collections if API returns empty
  const displayCollections = collections.length > 0 ? collections : [
    {
      handle: 'bracelets',
      title: 'Bracelets',
      description: 'Our signature collection of waterproof bracelets, featuring 18K gold plated and stainless steel designs.',
      image: { url: 'https://customer-assets.emergentagent.com/job_9ca05207-5a15-4698-a993-55e5ec27d3ee/artifacts/bopqzr53_0.jpg' }
    },
    {
      handle: 'earrings',
      title: 'Earrings',
      description: 'Elegant earrings designed to complement any outfit, from subtle studs to statement drops.',
      image: { url: 'https://images.unsplash.com/photo-1584948221378-93841f589fb7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHxkaWFtb25kJTIwZWFycmluZ3MlMjBsdXh1cnklMjBjbG9zZSUyMHVwfGVufDB8fHx8MTc2ODgyOTQ4OXww&ixlib=rb-4.1.0&q=85' }
    }
  ];

  return (
    <>
      <Helmet>
        <title>Collections - Bracelets & Earrings | LuxuryStyle</title>
        <meta 
          name="description" 
          content="Explore LuxuryStyle jewellery collections. Shop waterproof bracelets and tarnish-free earrings crafted with 18K gold plated stainless steel." 
        />
        <meta property="og:title" content="Collections - LuxuryStyle" />
        <meta property="og:description" content="Explore our curated jewellery collections." />
        <link rel="canonical" href="https://luxurystyle.info/collections" />
      </Helmet>

      <main data-testid="collections-page" className="pt-20">
        {/* Hero Section */}
        <section className="py-16 md:py-24 bg-[#0a0a0a] border-b border-white/5">
          <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
            <span className="text-[#D4AF37] font-accent text-xs uppercase tracking-[0.2em]">
              Explore
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-medium text-white mt-4 mb-4">
              Our Collections
            </h1>
            <div className="gold-divider mb-6" />
            <p className="text-white/60 max-w-2xl mx-auto">
              Discover our carefully curated collections, each designed to bring 
              timeless elegance to your everyday style.
            </p>
          </div>
        </section>

        {/* Collections Grid */}
        <section className="py-20 md:py-32 bg-[#050505]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {[1, 2].map((i) => (
                  <div key={i} className="animate-pulse">
                    <div className="aspect-[4/3] bg-[#111] mb-6" />
                    <div className="h-6 bg-[#111] w-1/3 mb-3" />
                    <div className="h-4 bg-[#111] w-2/3" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {displayCollections.map((collection) => (
                  <Link
                    key={collection.handle}
                    to={`/jewellery?collection=${collection.handle}`}
                    data-testid={`collection-${collection.handle}`}
                    className="group"
                  >
                    <div className="relative overflow-hidden aspect-[4/3] mb-6">
                      <img
                        src={collection.image?.url || 'https://images.unsplash.com/photo-1731406322274-fb018de6fa97?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxnb2xkJTIwYnJhY2VsZXQlMjB3b21hbiUyMHdyaXN0JTIwZGFyayUyMGZhc2hpb258ZW58MHx8fHwxNzY4ODI5NDg4fDA&ixlib=rb-4.1.0&q=85'}
                        alt={collection.image?.altText || collection.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                      <div className="absolute inset-4 border border-[#D4AF37]/0 group-hover:border-[#D4AF37]/30 transition-all duration-500 pointer-events-none" />
                      <div className="absolute bottom-6 left-6 right-6">
                        <h2 className="text-3xl md:text-4xl font-medium text-white mb-2">
                          {collection.title}
                        </h2>
                        <span className="inline-flex items-center space-x-2 text-[#D4AF37] group-hover:translate-x-2 transition-transform">
                          <span className="font-accent text-xs uppercase tracking-[0.2em]">
                            Shop Now
                          </span>
                          <ArrowRight className="w-4 h-4" />
                        </span>
                      </div>
                    </div>
                    {collection.description && (
                      <p className="text-white/60 text-sm leading-relaxed">
                        {collection.description}
                      </p>
                    )}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 md:py-32 bg-[#0a0a0a]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <div className="p-8">
                <h3 className="text-[#D4AF37] text-4xl font-medium mb-2">100%</h3>
                <p className="text-white/60 text-sm uppercase tracking-wider">Waterproof</p>
              </div>
              <div className="p-8 border-y md:border-y-0 md:border-x border-white/10">
                <h3 className="text-[#D4AF37] text-4xl font-medium mb-2">18K</h3>
                <p className="text-white/60 text-sm uppercase tracking-wider">Gold Plated</p>
              </div>
              <div className="p-8">
                <h3 className="text-[#D4AF37] text-4xl font-medium mb-2">∞</h3>
                <p className="text-white/60 text-sm uppercase tracking-wider">Tarnish-Free</p>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default CollectionsPage;
